import { OrbitMusicBox } from "./orbit/OrbitMusicBox";
import { OrbitMusicBoxV2 } from "./orbit/v2/OrbitMusicBoxV2";
import { OrbitMusicBoxV3 } from "./orbit/v3/OrbitMusicBoxV3";
import { DURATION as ORBIT_DURATION } from "./orbit/score";
import { FourierRhythmMechanical } from "./rhythm/FourierRhythmMechanical";
import { BloomingMandalaSpirograph } from "./mandala/BloomingMandalaSpirograph";
import { LastPiece } from "./packing/LastPiece";
import {
  EndCtaDemo,
  FourierHeartWithCta,
  InvaderHookBWithCta,
} from "./shared/EndCtaPreviews";
import { HookPrototype } from "./hooks/HookPrototype";
import { InvaderHookApplied } from "./hooks/InvaderHookApplied";
import { FourierDragonSingle } from "./fourier/FourierDragonSingle";
import { FourierDragon } from "./fourier/FourierDragon";
import { FourierInvader } from "./fourier/FourierInvader";
import { FourierCat } from "./fourier/FourierCat";
import { FourierPenguin } from "./fourier/FourierPenguin";
import { FourierDinosaur } from "./fourier/FourierDinosaur";
import { FourierKoi } from "./fourier/FourierKoi";
import { KOI_DURATION_SECONDS } from "./fourier/koiModel";
import { DINOSAUR_DURATION_SECONDS } from "./fourier/dinosaurTheme";
import { PENGUIN_DURATION_SECONDS } from "./fourier/penguinTheme";
import { CAT_DURATION_SECONDS } from "./fourier/catTheme";
import { Composition } from "remotion";
import { FourierHeart } from "./fourier/FourierHeart";
import { fourierConfig, durationSeconds } from "./fourier/config";
import { framesForSeconds, LEGACY_PREVIEW_FPS, PRODUCTION_FPS } from "./videoProfile";
import { MarbleFunnelExperiment } from "./marble/MarbleFunnelExperiment";

// Regular Fourier films render at 60 fps; frame-authored comparisons keep 30 fps.
export const RemotionRoot: React.FC = () => {
  return (
    <>
      <Composition
        id="FourierKoi"
        component={FourierKoi}
        durationInFrames={framesForSeconds(KOI_DURATION_SECONDS)}
        fps={PRODUCTION_FPS}
        width={1080}
        height={1920}
      />
      <Composition
        id="OrbitMusicBoxV3"
        component={OrbitMusicBoxV3}
        durationInFrames={1080}
        fps={60}
        width={1080}
        height={1920}
      />
      <Composition
        id="OrbitMusicBoxV2"
        component={OrbitMusicBoxV2}
        durationInFrames={1080}
        fps={60}
        width={1080}
        height={1920}
      />
      <Composition
        id="OrbitMusicBox"
        component={OrbitMusicBox}
        durationInFrames={framesForSeconds(ORBIT_DURATION)}
        fps={PRODUCTION_FPS}
        width={1080}
        height={1920}
      />
      <Composition
        id="FourierRhythmMechanical"
        component={FourierRhythmMechanical}
        durationInFrames={framesForSeconds(12)}
        fps={PRODUCTION_FPS}
        width={1080}
        height={1920}
      />
      <Composition
        id="BloomingMandalaSpirograph"
        component={BloomingMandalaSpirograph}
        durationInFrames={framesForSeconds(12)}
        fps={PRODUCTION_FPS}
        width={1080}
        height={1920}
      />
      <Composition
        id="MarbleFunnelExperiment"
        component={MarbleFunnelExperiment}
        durationInFrames={720}
        fps={60}
        width={1080}
        height={1920}
      />
      <Composition
        id="LastPiecePrototype"
        component={LastPiece}
        durationInFrames={framesForSeconds(10, LEGACY_PREVIEW_FPS)}
        fps={LEGACY_PREVIEW_FPS}
        width={1080}
        height={1920}
      />
      <Composition
        id="EndCtaGlassDemo"
        component={EndCtaDemo}
        durationInFrames={framesForSeconds(2.5, LEGACY_PREVIEW_FPS)}
        fps={LEGACY_PREVIEW_FPS}
        width={1080}
        height={1920}
        defaultProps={{ variant: "glass" }}
      />
      <Composition
        id="EndCtaMinimalDemo"
        component={EndCtaDemo}
        durationInFrames={framesForSeconds(2.5, LEGACY_PREVIEW_FPS)}
        fps={LEGACY_PREVIEW_FPS}
        width={1080}
        height={1920}
        defaultProps={{ variant: "minimal" }}
      />
      <Composition
        id="FourierHeartCtaPreview"
        component={FourierHeartWithCta}
        durationInFrames={framesForSeconds(20)}
        fps={PRODUCTION_FPS}
        width={1080}
        height={1920}
      />
      <Composition
        id="FourierInvaderHookBCtaPreview"
        component={InvaderHookBWithCta}
        durationInFrames={framesForSeconds(18.7, LEGACY_PREVIEW_FPS)}
        fps={LEGACY_PREVIEW_FPS}
        width={1080}
        height={1920}
      />
      <Composition
        id="FourierInvaderHookBPreview"
        component={InvaderHookApplied}
        durationInFrames={framesForSeconds(18.7, LEGACY_PREVIEW_FPS)}
        fps={LEGACY_PREVIEW_FPS}
        width={1080}
        height={1920}
        defaultProps={{ pattern: "B" }}
      />
      <Composition
        id="FourierInvaderHookAPreview"
        component={InvaderHookApplied}
        durationInFrames={framesForSeconds(18.7, LEGACY_PREVIEW_FPS)}
        fps={LEGACY_PREVIEW_FPS}
        width={1080}
        height={1920}
      />
      <Composition
        id="InvaderHookA"
        component={HookPrototype}
        durationInFrames={framesForSeconds(4, LEGACY_PREVIEW_FPS)}
        fps={LEGACY_PREVIEW_FPS}
        width={720}
        height={1280}
        defaultProps={{ pattern: "A" }}
      />
      <Composition
        id="InvaderHookB"
        component={HookPrototype}
        durationInFrames={framesForSeconds(4, LEGACY_PREVIEW_FPS)}
        fps={LEGACY_PREVIEW_FPS}
        width={720}
        height={1280}
        defaultProps={{ pattern: "B" }}
      />
      <Composition
        id="InvaderHookC"
        component={HookPrototype}
        durationInFrames={framesForSeconds(4, LEGACY_PREVIEW_FPS)}
        fps={LEGACY_PREVIEW_FPS}
        width={720}
        height={1280}
        defaultProps={{ pattern: "C" }}
      />
      <Composition
        id="InvaderHookD"
        component={HookPrototype}
        durationInFrames={framesForSeconds(4, LEGACY_PREVIEW_FPS)}
        fps={LEGACY_PREVIEW_FPS}
        width={720}
        height={1280}
        defaultProps={{ pattern: "D" }}
      />
      <Composition
        id="InvaderHookE"
        component={HookPrototype}
        durationInFrames={framesForSeconds(4, LEGACY_PREVIEW_FPS)}
        fps={LEGACY_PREVIEW_FPS}
        width={720}
        height={1280}
        defaultProps={{ pattern: "E" }}
      />
      <Composition
        id="FourierDragonSingle"
        component={FourierDragonSingle}
        durationInFrames={framesForSeconds(20)}
        fps={PRODUCTION_FPS}
        width={1080}
        height={1920}
      />
      <Composition
        id="FourierDragon"
        component={FourierDragon}
        durationInFrames={framesForSeconds(36)}
        fps={PRODUCTION_FPS}
        width={1080}
        height={1920}
      />
      <Composition
        id="FourierInvader"
        component={FourierInvader}
        durationInFrames={framesForSeconds(20)}
        fps={PRODUCTION_FPS}
        width={1080}
        height={1920}
      />
      <Composition
        id="FourierDinosaur"
        component={FourierDinosaur}
        durationInFrames={framesForSeconds(DINOSAUR_DURATION_SECONDS)}
        fps={PRODUCTION_FPS}
        width={1080}
        height={1920}
      />
      <Composition
        id="FourierPenguin"
        component={FourierPenguin}
        durationInFrames={framesForSeconds(PENGUIN_DURATION_SECONDS)}
        fps={PRODUCTION_FPS}
        width={1080}
        height={1920}
      />
      <Composition
        id="FourierCat"
        component={FourierCat}
        durationInFrames={framesForSeconds(CAT_DURATION_SECONDS)}
        fps={PRODUCTION_FPS}
        width={1080}
        height={1920}
      />
      <Composition
        id="FourierHeart"
        component={FourierHeart}
        durationInFrames={framesForSeconds(20)}
        fps={PRODUCTION_FPS}
        width={1080}
        height={1920}
        calculateMetadata={() => ({
          ...fourierConfig.video,
          durationInFrames: Math.round(
            durationSeconds * fourierConfig.video.fps,
          ),
        })}
      />
    </>
  );
};



